import {
  EvalAccountByDatePaPayoutNewJodyTable,
  mapEvalsToPaItem,
} from '@features/admin/reports';
import { useState } from 'react';
import { DateValueType, downloadFile } from '@lib/utils';
import { TReportEvalsToPaRes } from '@src/types/reports';
import { exportEvalsToPa, getEvalsToPa } from '@src/services/reports';
import { useProductOptions } from '@hooks/useProductOptions';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import { TEvalAccountByDatePaPayoutNewJodyTable } from '@src/types';

interface ReportSummary {
  packageLabel: string;
  fromDate: string;
  toDate: string;
  generatedAt: string;
}

export const EvalAccountByDatePaPayoutNewJody = () => {
  const [selectedPackage, setSelectedPackage] = useState('');
  const [valueStart, setValueStart] = useState<DateValueType>(null);
  const [valueEnd, setValueEnd] = useState<DateValueType>(null);
  const [tableData, setTableData] = useState<
    TEvalAccountByDatePaPayoutNewJodyTable[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const [reportSummary, setReportSummary] = useState<ReportSummary | null>(
    null
  );

  const { productOptions } = useProductOptions();

  const buildParams = () => ({
    package_id: selectedPackage ? Number(selectedPackage) : undefined,
    from_date: valueStart?.startDate
      ? dayjs(valueStart.startDate).format('YYYY-MM-DD')
      : undefined,
    to_date: valueEnd?.startDate
      ? dayjs(valueEnd.startDate).format('YYYY-MM-DD')
      : undefined,
  });

  const handleGetReport = async () => {
    setIsLoading(true);
    try {
      const res: TReportEvalsToPaRes = await getEvalsToPa(buildParams());

      setTableData(res.data.map(mapEvalsToPaItem));
      const selectedLabel =
        productOptions.find((o) => o.value === selectedPackage)?.label ??
        'All Packages';

      setReportSummary({
        packageLabel: selectedLabel,
        fromDate: valueStart?.startDate
          ? dayjs(valueStart.startDate).format('DD MMM YYYY')
          : '—',
        toDate: valueEnd?.startDate
          ? dayjs(valueEnd.startDate).format('DD MMM YYYY')
          : '—',
        generatedAt: dayjs(res.generated_at).format('DD MMM YYYY HH:mm'),
      });
      toast.success('Report generated successfully!');
    } catch {
      toast.error('Failed to generate report');
    } finally {
      setIsLoading(false);
    }
  };

  const handleExportReport = async () => {
    try {
      const res = await exportEvalsToPa(buildParams());

      downloadFile(res.data, 'evals-account-to-pa');
      toast.success('Report exported successfully!');
    } catch {
      toast.error('Failed to export report');
    }
  };

  return (
    <div>
      <div
        className={
          'flex lg:flex-row flex-col justify-between lg:items-center gap-7.5'
        }
      >
        <div className={'text-xs flex lg:flex-row flex-col gap-x-8 gap-y-3'}>
          <h1
            className={'text-[23px]'}
          >{`EVAL - Account By Date > PA > Payout (New - Jody)`}</h1>
          {reportSummary && (
            <p className={'max-w-[419px]'}>
              {reportSummary.packageLabel}, created between{' '}
              {reportSummary.fromDate} and {reportSummary.toDate}, how many
              converted to PA, how many got payouts. Report run on{' '}
              {reportSummary.generatedAt} MST
            </p>
          )}
        </div>
      </div>
      <EvalAccountByDatePaPayoutNewJodyTable
        data={tableData}
        isLoading={isLoading}
        productOptions={productOptions}
        selectedPackage={selectedPackage}
        setSelectedPackage={setSelectedPackage}
        setValueEnd={setValueEnd}
        setValueStart={setValueStart}
        valueEnd={valueEnd}
        valueStart={valueStart}
        onExport={handleExportReport}
        onGet={handleGetReport}
      />
    </div>
  );
};
